import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {Button} from 'primereact/button';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Dialog} from 'primereact/dialog';
import {Dropdown} from 'primereact/dropdown';
import {InputNumber} from 'primereact/inputnumber';

const emptyRule = {
  production_item_status: null,
  technology: null,
  match_strategy: 'ANY',
  priority: 100,
};

const ProjectitemstatusRuleEditorComponent = ({projectItemStatusId, t}) => {
  const [rules, setRules] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [productionStatuses, setProductionStatuses] = useState([]);
  const [rule, setRule] = useState(emptyRule);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const apiCall = useCallback((method, url, data) => axios({
    method,
    url: process.env.REACT_APP_API_HOST + url,
    data,
    headers: {
      'Authorization': 'Bearer ' + localStorage.getItem('userToken'),
      'App-Network': localStorage.getItem('networkId'),
    },
  }), []);

  const loadRules = useCallback(() => {
    if (!projectItemStatusId) {
      return Promise.resolve();
    }

    setLoading(true);
    return apiCall('get', '/api/v2/project-item-status-rule?item_per_page=1000')
      .then(response => {
        const data = response.data.data?.items || [];
        setRules(data.filter(item => item.project_item_status?.id === Number(projectItemStatusId)));
      })
      .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setLoading(false));
  }, [apiCall, projectItemStatusId, t]);

  useEffect(() => {
    loadRules();
    apiCall('get', '/api/v2/dictionary/production-status')
      .then(response => setProductionStatuses(response.data.data || []));
    apiCall('get', '/api/v2/technology?item_per_page=1000')
      .then(response => setTechnologies(response.data.data?.items || []));
  }, [apiCall, loadRules]);

  const openNew = () => {
    setRule({...emptyRule});
    setVisible(true);
  };

  const openEdit = row => {
    setRule({
      id: row.id,
      production_item_status: row.production_item_status,
      technology: row.technology?.id ?? null,
      match_strategy: row.match_strategy,
      priority: row.priority,
    });
    setVisible(true);
  };

  const save = () => {
    const payload = {
      production_item_status: rule.production_item_status,
      project_item_status: Number(projectItemStatusId),
      technology: rule.technology,
      match_strategy: rule.match_strategy,
      priority: rule.priority,
    };
    const request = rule.id
      ? apiCall('patch', '/api/v2/project-item-status-rule/' + rule.id, payload)
      : apiCall('post', '/api/v2/project-item-status-rule', payload);

    setLoading(true);
    request
      .then(() => {
        window.App.toastShow('success', 'Sikeres mentés!');
        setVisible(false);
        return loadRules();
      })
      .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setLoading(false));
  };

  const remove = row => {
    if (!window.confirm('Biztosan törlöd ezt a szabályt?')) {
      return;
    }
    setLoading(true);
    apiCall('delete', '/api/v2/project-item-status-rule/' + row.id)
      .then(loadRules)
      .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setLoading(false));
  };

  return (
    <div className="p-mt-4">
      <div className="p-d-flex p-jc-between p-ai-center p-mb-2">
        <h3 className="p-m-0">Projekttétel-státusz szabályok</h3>
        <Button type="button" label="Új szabály" icon="pi pi-plus" onClick={openNew}/>
      </div>
      <DataTable value={rules} loading={loading} emptyMessage="Nincs beállított szabály." responsiveLayout="scroll">
        <Column field="production_item_status" header="ProductionItem státusz"/>
        <Column header="Technológia" body={row => row.technology?.name || 'Bármely technológia'}/>
        <Column field="match_strategy" header="Illesztés"/>
        <Column field="priority" header="Prioritás"/>
        <Column body={row => (
          <div className="p-d-flex p-jc-end">
            <Button type="button" icon="pi pi-pencil" className="p-button-text" onClick={() => openEdit(row)}/>
            <Button type="button" icon="pi pi-trash" className="p-button-text p-button-danger" onClick={() => remove(row)}/>
          </div>
        )}/>
      </DataTable>

      <Dialog visible={visible} header={rule.id ? 'Szabály módosítása' : 'Új szabály'} modal
              style={{width: 'min(42rem, 95vw)'}} onHide={() => setVisible(false)}>
        <div className="p-fluid">
          <div className="p-field">
            <label htmlFor="rule-production-status">ProductionItem státusz</label>
            <Dropdown id="rule-production-status" value={rule.production_item_status}
                      options={productionStatuses} optionLabel="value" optionValue="key"
                      onChange={e => setRule({...rule, production_item_status: e.value})}/>
          </div>
          <div className="p-field">
            <label htmlFor="rule-technology">Technológia (opcionális)</label>
            <Dropdown id="rule-technology" value={rule.technology} options={technologies}
                      optionLabel="name" optionValue="id" filter showClear placeholder="Bármely technológia"
                      onChange={e => setRule({...rule, technology: e.value})}/>
          </div>
          <div className="p-field">
            <label htmlFor="rule-match-strategy">Illesztés</label>
            <Dropdown id="rule-match-strategy" value={rule.match_strategy}
                      options={[{label: 'Bármely (ANY)', value: 'ANY'}, {label: 'Mindegyik (ALL)', value: 'ALL'}]}
                      onChange={e => setRule({...rule, match_strategy: e.value})}/>
          </div>
          <div className="p-field">
            <label htmlFor="rule-priority">Prioritás</label>
            <InputNumber id="rule-priority" value={rule.priority}
                         onValueChange={e => setRule({...rule, priority: e.value})}/>
          </div>
        </div>
        <div className="p-d-flex p-jc-end p-mt-3">
          <Button type="button" label="Mégse" className="p-button-text p-mr-2" onClick={() => setVisible(false)}/>
          <Button type="button" label="Mentés" icon="pi pi-save" loading={loading}
                  disabled={!rule.production_item_status || !rule.match_strategy || rule.priority === null}
                  onClick={save}/>
        </div>
      </Dialog>
    </div>
  );
};

export default ProjectitemstatusRuleEditorComponent;
